import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Text, VStack } from "@chakra-ui/react";
import type { Dayjs } from "dayjs";
import { SCHEDULED_BUDGET_MAX_SEGMENTS } from "@common/utils/campaignBudgetMeta";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { BudgetData, BudgetPhaseForm } from "./BudgetSelector";
import type { PhaseCalendarNeighborBounds } from "./BudgetPhaseMetaDateTimeRange";
import { guidedBudgetPrimaryColor } from "./guidedBudgetEditorTokens";
import { GuidedBudgetPhaseRow } from "./GuidedBudgetPhaseRow";
import type { ScheduledBudgetAdFlightUtcBounds } from "../../utils/eventWallTime";

type GuidedBudgetFieldOutline =
  | { kind: "time_start" | "time_end"; segmentIndex: number }
  | { kind: "amount"; segmentIndex: number };

type GuidedBudgetScheduledPhasesSectionProps = {
  scheduledSegments: BudgetPhaseForm[];
  eventTimezone: string;
  flightRangeInEventZone: { start: Dayjs; end: Dayjs } | null;
  scheduledBudgetFlightBounds: ScheduledBudgetAdFlightUtcBounds | null;
  phaseNeighborPickerBounds: Array<PhaseCalendarNeighborBounds | null>;
  scheduledPhaseTimelineError: string | null;
  scheduledBudgetSegmentRowErrorMessages?: Map<number, string>;
  scheduledBudgetFieldOutline?: GuidedBudgetFieldOutline | null;
  budget?: BudgetData;
  onAddSegment?: () => void;
  onRemoveSegment?: (index: number) => void;
  onChangeSegment?: (index: number, patch: Partial<BudgetPhaseForm>) => void;
};

export function GuidedBudgetScheduledPhasesSection({
  scheduledSegments,
  eventTimezone,
  flightRangeInEventZone,
  scheduledBudgetFlightBounds,
  phaseNeighborPickerBounds,
  scheduledPhaseTimelineError,
  scheduledBudgetSegmentRowErrorMessages,
  scheduledBudgetFieldOutline,
  budget,
  onAddSegment,
  onRemoveSegment,
  onChangeSegment,
}: GuidedBudgetScheduledPhasesSectionProps) {
  const atMaxPhases = scheduledSegments.length >= SCHEDULED_BUDGET_MAX_SEGMENTS;
  const hasPerRowErrors = Boolean(scheduledBudgetSegmentRowErrorMessages?.size);
  const showGlobalTimelineAlert =
    Boolean(scheduledPhaseTimelineError) && !hasPerRowErrors;
  return (
    <>
      <Box h="1px" bg="gray.200" w="full" flexShrink={0} />

      {scheduledSegments.length < 2 ? (
        <Text fontSize="sm" color="gray.600" lineHeight="short" px={0.5}>
          Scheduled budgets need at least two phases. Set{" "}
          <Text as="span" fontWeight="semibold">
            Campaign run dates
          </Text>{" "}
          above so phase times can populate, or use{" "}
          <Text as="span" fontWeight="semibold">
            Add Budget Phase
          </Text>
          .
        </Text>
      ) : null}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <VStack align="stretch" spacing={3} w="full" pb={3}>
          {scheduledSegments.map((s, index) => (
            <GuidedBudgetPhaseRow
              key={index}
              index={index}
              segment={s}
              segmentCount={scheduledSegments.length}
              eventTimezone={eventTimezone}
              flightRangeInEventZone={flightRangeInEventZone}
              neighborPickerBounds={phaseNeighborPickerBounds[index] ?? null}
              scheduledBudgetFlightBounds={scheduledBudgetFlightBounds}
              budget={budget}
              rowValidationMessage={
                scheduledBudgetSegmentRowErrorMessages?.get(index) ?? null
              }
              invalidTimeField={
                scheduledBudgetFieldOutline?.segmentIndex === index &&
                (scheduledBudgetFieldOutline.kind === "time_start" ||
                  scheduledBudgetFieldOutline.kind === "time_end")
                  ? scheduledBudgetFieldOutline.kind === "time_start"
                    ? "start"
                    : "end"
                  : null
              }
              invalidAmount={
                scheduledBudgetFieldOutline?.segmentIndex === index &&
                scheduledBudgetFieldOutline.kind === "amount"
              }
              onChangeSegment={onChangeSegment}
              onRemoveSegment={onRemoveSegment}
            />
          ))}
        </VStack>
        {showGlobalTimelineAlert ? (
          <Text
            fontSize="sm"
            color="red.600"
            lineHeight="short"
            px={0.5}
            role="alert"
          >
            {scheduledPhaseTimelineError}
          </Text>
        ) : null}
        <Text fontSize="xs" color="gray.500" lineHeight="short" px={0.5}>
          {`Phases snap to 15-minute intervals and must be at least 3 hours long, within your campaign's run dates. You can add up to ${SCHEDULED_BUDGET_MAX_SEGMENTS} phases.`}
        </Text>
      </LocalizationProvider>

      <Button
        alignSelf="center"
        bg={guidedBudgetPrimaryColor}
        color="white"
        borderRadius="10px"
        px={3}
        py={2.5}
        h="auto"
        fontWeight="medium"
        fontSize="sm"
        lineHeight="19.2px"
        borderWidth="1px"
        borderColor="#E2E2E2"
        boxShadow="0px 2px 6px rgba(0, 0, 0, 0.05)"
        _hover={{ bg: "gray.800" }}
        _active={{ bg: "gray.900" }}
        rightIcon={<AddIcon boxSize={4} />}
        isDisabled={atMaxPhases}
        onClick={() => onAddSegment?.()}
      >
        Add Budget Phase
      </Button>
    </>
  );
}
